import React, { useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { addDays, addMonths, format, isValid, parseISO } from 'date-fns'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'

import { ChevronLeftIcon, ChevronRightIcon } from '@dfds-frontend/navigator-icons/icons'
import { ButtonClick, track } from '@dfds-frontend/tracking'
import { Button, DateFieldControlled, DatesData, Label } from '@dfds-pax/shared-components/client'
import { getEconomyProduct } from '@dfds-pax/utils'

import { getAvailableTravelDates } from '@/app/[locale]/[ferry]/_actions/getAvailableTravelDates'
import { pickFrontendLog } from '@/app/[locale]/[ferry]/_lib/frontendLogging'
import { loadingAtom } from '@/states/loadingAtoms'
import {
  selectedDateAtom,
  selectedDeparturesAtom,
  selectedOfferAtom,
  selectedPassengersAtom,
  selectedRoutesAtom,
  selectedVehiclesAtom,
} from '@/states/selectionAtom'

import { getCompoundRoute, getDatepickerLocale, getUrlLocale } from '@/utils'

import { useTranslation } from '@/hooks/useDebugTranslations'

import { datesMapper } from './datesMapper'
import DepartureRouteFilters, { RouteFilter } from './RouteFilters'
import TimeFilters, { TimeFilter } from './TimeFilters'

const CURRENT_DATE = new Date()
const CURRENT_DATE_STRING = format(CURRENT_DATE, 'y-MM-dd')
const CURRENT_DATE_PLUS_ONE_MONTH_STRING = format(addMonths(CURRENT_DATE, 1), 'y-MM-dd')

const EMPTY_DATES_DATA: DatesData = {
  dates: {
    fromDate: CURRENT_DATE_STRING,
    toDate: CURRENT_DATE_PLUS_ONE_MONTH_STRING,
  },
  defaultDate: CURRENT_DATE_STRING,
  disabledDates: [],
  offerDates: [],
}

interface DateCalendarComponentProps {
  className?: string
  dateClicked: (date: string, legNumber: 1 | 2, direction?: 'left' | 'right') => void
  datesData: DatesData
  departureDate: string
  onDateFieldClick: () => void | Promise<void>
  returnLeg?: boolean
  trackDepartureDateChanged: (returnLeg?: boolean, direction?: 'previousdate' | 'nextdate') => void
}

const trackDepartureDateChanged = (returnLeg?: boolean, direction?: 'previousdate' | 'nextdate') =>
  track<ButtonClick>({
    clickedElementText: direction || '',
    event: 'buttonClick',
    eventAction: `DepartureDate${returnLeg ? 'Return' : 'Outward'}Clicked`,
  })

interface FiltersProps {
  dateChanged: (date: string, legNumber: 1 | 2) => void
  departureDate: string
  isJersey?: boolean
  onRouteFilterChange: (filters: RouteFilter) => void
  onTimeFilterChange: (filters: TimeFilter) => void
  returnLeg?: boolean
  selectedRouteFilters: RouteFilter
  selectedTimeFilters: TimeFilter
}

export const Filters = ({
  dateChanged,
  departureDate,
  isJersey,
  onRouteFilterChange,
  onTimeFilterChange,
  returnLeg,
  selectedRouteFilters,
  selectedTimeFilters,
}: FiltersProps): React.ReactElement => {
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom)
  const setIsLoadingState = useSetAtom(loadingAtom)
  const [selectedRouteDatesData, setSelectedRouteDatesData] = useState<DatesData>(EMPTY_DATES_DATA)
  const [compoundRouteDatesData, setCompoundRouteDatesData] = useState<DatesData>(EMPTY_DATES_DATA)
  const dateChangedTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingDateRef = useRef<string | null>(null)
  const selectedVehicles = useAtomValue(selectedVehiclesAtom)
  const selectedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedPassengers = useAtomValue(selectedPassengersAtom)
  const selectedOfferCode = useAtomValue(selectedOfferAtom)
  const selectedDepartures = useAtomValue(selectedDeparturesAtom)
  const compoundRoute = getCompoundRoute(returnLeg ? selectedRoutes.homebound : selectedRoutes.outbound)

  const locale = getUrlLocale()

  const refreshDatesBeforeDatepickerOpen = async () => {
    const outboundDateForRequest = selectedDepartures.departureOut?.departureDate ?? selectedDate?.outboundDepartureDate ?? undefined

    try {
      const [mainRouteDatesData, compoundRouteDatesResponse] = await Promise.all([
        getAvailableTravelDates(
          locale,
          selectedRoutes.outbound,
          returnLeg,
          selectedPassengers.adults,
          selectedPassengers.children,
          selectedPassengers.infants,
          selectedVehicles.vehicleCode,
          selectedOfferCode.offerCode ?? getEconomyProduct(selectedRoutes.outbound),
          selectedVehicles.trailerCode,
          selectedPassengers.childrenAges,
          outboundDateForRequest
        ),
        compoundRoute
          ? getAvailableTravelDates(
              locale,
              compoundRoute,
              returnLeg,
              selectedPassengers.adults,
              selectedPassengers.children,
              selectedPassengers.infants,
              selectedVehicles.vehicleCode,
              selectedOfferCode.offerCode ?? getEconomyProduct(selectedRoutes.outbound),
              selectedVehicles.trailerCode,
              selectedPassengers.childrenAges,
              outboundDateForRequest
            )
          : Promise.resolve(undefined),
      ])

      setSelectedRouteDatesData(mainRouteDatesData)

      if (compoundRouteDatesResponse) {
        setCompoundRouteDatesData(compoundRouteDatesResponse)
      } else {
        setCompoundRouteDatesData(EMPTY_DATES_DATA)
      }
    } catch (error) {
      const errorMessage = (error as Error)?.message
      const log = pickFrontendLog(errorMessage)
      log('Error refreshing travel dates before opening datepicker', { errorMessage })
    }
  }

  // Fetch main route travel dates
  useEffect(() => {
    const fetchMainRouteDates = async () => {
      try {
        const data = await getAvailableTravelDates(
          locale,
          selectedRoutes.outbound,
          returnLeg,
          selectedPassengers.adults,
          selectedPassengers.children,
          selectedPassengers.infants,
          selectedVehicles.vehicleCode,
          selectedOfferCode.offerCode ?? getEconomyProduct(selectedRoutes.outbound),
          selectedVehicles.trailerCode,
          selectedPassengers.childrenAges,
          selectedDepartures.departureOut?.departureDate ?? selectedDate?.outboundDepartureDate ?? undefined
        )
        setSelectedRouteDatesData(data)
      } catch (error) {
        const errorMessage = (error as Error)?.message
        const log = pickFrontendLog(errorMessage)
        log('Error fetching travel dates for main route', { errorMessage })
      }
    }

    fetchMainRouteDates()
  }, [
    locale,
    selectedRoutes.outbound,
    returnLeg,
    selectedPassengers.adults,
    selectedPassengers.children,
    selectedPassengers.infants,
    selectedVehicles.vehicleCode,
    selectedOfferCode.offerCode,
    selectedVehicles.trailerCode,
    selectedPassengers.childrenAges,
    selectedDepartures.departureOut?.departureDate,
    selectedDate?.outboundDepartureDate,
  ])

  // Fetch compound route travel dates if it exists
  useEffect(() => {
    if (!compoundRoute) {
      setCompoundRouteDatesData(EMPTY_DATES_DATA)
      return
    }

    const fetchCompoundRouteDates = async () => {
      try {
        const data = await getAvailableTravelDates(
          locale,
          compoundRoute,
          returnLeg,
          selectedPassengers.adults,
          selectedPassengers.children,
          selectedPassengers.infants,
          selectedVehicles.vehicleCode,
          selectedOfferCode.offerCode ?? getEconomyProduct(selectedRoutes.outbound),
          selectedVehicles.trailerCode,
          selectedPassengers.childrenAges,
          selectedDepartures.departureOut?.departureDate ?? selectedDate?.outboundDepartureDate ?? undefined
        )
        setCompoundRouteDatesData(data)
      } catch (error) {
        const errorMessage = (error as Error)?.message
        const log = pickFrontendLog(errorMessage)
        log('Error fetching travel dates for compound route', { errorMessage })
      }
    }

    fetchCompoundRouteDates()
  }, [
    locale,
    compoundRoute,
    returnLeg,
    selectedPassengers.adults,
    selectedPassengers.children,
    selectedPassengers.infants,
    selectedVehicles.vehicleCode,
    selectedOfferCode.offerCode,
    selectedVehicles.trailerCode,
    selectedPassengers.childrenAges,
    selectedDepartures.departureOut?.departureDate,
    selectedDate?.outboundDepartureDate,
    selectedRoutes.outbound,
  ])

  // Calculate final dates data based on compound route or main route
  let datesData: DatesData = selectedRouteDatesData
  if (compoundRoute && selectedRouteDatesData !== EMPTY_DATES_DATA && compoundRouteDatesData !== EMPTY_DATES_DATA) {
    datesData = datesMapper(
      selectedRouteDatesData,
      compoundRouteDatesData,
      returnLeg ?? false,
      compoundRoute,
      selectedRouteFilters,
      selectedDepartures.departureOut?.departureDateTimeForBasket
    )
  }

  const dateClicked = (date: string, legNumber: 1 | 2, direction?: 'left' | 'right') => {
    const newDate = new Date(date)
    if (direction === 'left') date = format(addDays(newDate, -1), 'y-MM-dd')
    if (direction === 'right') date = format(addDays(newDate, 1), 'y-MM-dd')

    pendingDateRef.current = date

    if (dateChangedTimeoutRef.current) {
      clearTimeout(dateChangedTimeoutRef.current)
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }

    loadingTimeoutRef.current = setTimeout(() => {
      if (pendingDateRef.current === date) {
        setIsLoadingState({ legNumber: legNumber, newDateLoading: true })
      }
    }, 150)

    if (legNumber === 1) {
      selectedDate &&
        setSelectedDate({
          outboundDepartureDate: date,
          returnDepartureDate: !selectedDate.returnDepartureDate ? null : selectedDate.returnDepartureDate,
        })
    }
    if (legNumber === 2) {
      selectedDate &&
        setSelectedDate({
          outboundDepartureDate:
            selectedDate.outboundDepartureDate && selectedDate.outboundDepartureDate > date ? date : selectedDate.outboundDepartureDate,
          returnDepartureDate: date,
        })
    }

    dateChangedTimeoutRef.current = setTimeout(() => {
      if (pendingDateRef.current !== date) {
        return
      }
      dateChanged(date, legNumber)
    }, 500)
  }

  useEffect(() => {
    return () => {
      if (dateChangedTimeoutRef.current) {
        clearTimeout(dateChangedTimeoutRef.current)
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="md:gap-semantic-global-lg gap-semantic-global-md mt-semantic-global-md flex flex-col md:grid md:grid-cols-2">
      <DateCalendarComponent
        className="order-1 md:col-span-1"
        dateClicked={dateClicked}
        datesData={datesData}
        departureDate={departureDate}
        onDateFieldClick={refreshDatesBeforeDatepickerOpen}
        returnLeg={returnLeg}
        trackDepartureDateChanged={trackDepartureDateChanged}
      />
      {compoundRoute ? (
        <RouteFilterComponent
          className={clsx('order-2', isJersey || !compoundRoute ? 'md:order-3' : 'md:order-2 md:col-span-1')}
          onRouteFilterChange={onRouteFilterChange}
          returnLeg={returnLeg}
          selectedRouteFilters={selectedRouteFilters}
        />
      ) : null}
      <TimeFilterComponent
        className={clsx('order-3', isJersey || !compoundRoute ? 'md:order-2 md:col-span-1' : 'md:order-3 md:col-span-2')}
        onTimeFilterChange={onTimeFilterChange}
        returnLeg={returnLeg}
        selectedTimeFilters={selectedTimeFilters}
      />
    </div>
  )
}

export const TimeFilterComponent = ({
  className,
  onTimeFilterChange,
  returnLeg,
  selectedTimeFilters,
}: {
  className?: string
  onTimeFilterChange: (filters: TimeFilter) => void
  returnLeg?: boolean
  selectedTimeFilters: TimeFilter
}) => {
  const { t } = useTranslation()

  return (
    <div className={clsx('gap-semantic-global-2xs flex flex-col', className)}>
      <div className="flex flex-row items-center">
        <Label>{t('FILTER-BY-TIME')}</Label>
        <button
          className={clsx(
            'text-semantic-global-primary-default typography-body-sm-medium ml-auto cursor-pointer',
            selectedTimeFilters.all && 'hidden'
          )}
          onClick={() => {
            track<ButtonClick>({
              clickedElementText: 'all',
              event: 'buttonClick',
              eventAction: `FilterDeparture${returnLeg ? 'Return' : 'Outward'}Clicked`,
            })
            onTimeFilterChange({ afternoon: false, all: true, evening: false, morning: false, night: false })
          }}
          type="button"
        >
          {t('CLEAR')}
        </button>
      </div>
      <TimeFilters onTimeFilterChange={onTimeFilterChange} returnLeg={returnLeg} selectedTimeFilters={selectedTimeFilters} />
    </div>
  )
}

export const RouteFilterComponent = ({
  className,
  onRouteFilterChange,
  returnLeg,
  selectedRouteFilters,
}: {
  className?: string
  onRouteFilterChange: (route: RouteFilter) => void
  returnLeg?: boolean
  selectedRouteFilters: RouteFilter
}) => {
  const { t } = useTranslation()

  return (
    <div className={clsx('gap-semantic-global-2xs flex flex-1 flex-col', className)}>
      <Label>{t('ROUTE-OPTIONS')}</Label>
      <DepartureRouteFilters onRouteFilterChange={onRouteFilterChange} returnLeg={returnLeg} selectedRouteFilters={selectedRouteFilters} />
    </div>
  )
}

export const DateCalendarComponent = ({
  className,
  dateClicked,
  datesData,
  departureDate,
  onDateFieldClick,
  returnLeg,
  trackDepartureDateChanged,
}: DateCalendarComponentProps): React.ReactElement => {
  const { t } = useTranslation()

  const isDateEnabled = (date: string): boolean => {
    const parsedDate = parseISO(date)
    if (!isValid(parsedDate)) {
      return false
    }

    return date >= datesData.dates.fromDate
  }

  const previousDate = format(addDays(new Date(departureDate), -1), 'y-MM-dd')
  const nextDate = format(addDays(new Date(departureDate), 1), 'y-MM-dd')
  const isPreviousDisabled = !isDateEnabled(previousDate)
  const isNextDisabled = nextDate > datesData.dates.toDate

  return (
    <div className={clsx('gap-semantic-global-2xs flex flex-1 flex-col', className)}>
      <Label>{t('SELECT-DATE')}</Label>
      <div className="gap-semantic-global-sm flex w-full items-center justify-between">
        <Button
          icon={<ChevronLeftIcon className="text-component-button-on-light-secondary-default" size="md" />}
          variant="secondary"
          className="bg-semantic-global-background-white! min-h-10.5"
          data-e2e={`date-selector-left-${returnLeg ? 'home' : 'out'}`}
          disabled={isPreviousDisabled}
          onClick={() => {
            dateClicked(departureDate, !returnLeg ? 1 : 2, 'left')
            trackDepartureDateChanged(returnLeg, 'previousdate')
          }}
          type="button"
        />

        <div className="flex-1" onClick={() => void onDateFieldClick()}>
          <DateFieldControlled
            datesData={datesData}
            value={departureDate}
            label={t('SELECT-DATE')}
            hideLabel={true}
            locale={getDatepickerLocale()}
            name={`departure-date-${returnLeg ? 'home' : 'out'}`}
            onDateChanged={(date) => {
              if (!date) return
              dateClicked(format(date, 'y-MM-dd'), !returnLeg ? 1 : 2)
            }}
          />
        </div>

        <Button
          icon={<ChevronRightIcon className="text-component-button-on-light-secondary-default" size="md" />}
          variant="secondary"
          className="bg-semantic-global-background-white! min-h-10.5"
          disabled={isNextDisabled}
          data-e2e={`date-selector-right-${returnLeg ? 'home' : 'out'}`}
          onClick={() => {
            dateClicked(departureDate, !returnLeg ? 1 : 2, 'right')
            trackDepartureDateChanged(returnLeg, 'nextdate')
          }}
          type="button"
        />
      </div>
    </div>
  )
}
